/**
 * @file src/api/client.api.js
 * @description All client-related API calls.
 *
 * Rules:
 *  - No try/catch — ApiError propagates from lib/axios.js interceptor
 *  - No console.log
 *  - No React, no hooks, no state
 */

import { apiClient } from "lib/axios";

/**
 * Create a new client.
 * @param {object} body - Client payload
 * @returns {Promise<object>} Created client
 */
export const createClient = async (body) => {
  const { data } = await apiClient.post("/clients/", body);
  return data;
};

/**
 * Fetch clients for the selected company.
 * Backend derives companyId from the selected-company cookie.
 * @param {object} params
 * @returns {Promise<object>} Client list response
 */
export const getClients = async (params = {}) => {
  const { data } = await apiClient.get("/clients/", {
    params,
  });
  return data;
};

/**
 * @param {string} query - Search term
 */
export const searchClients = async (query) => {
  const { data } = await apiClient.get("/clients/", {
    params: { search: query, limit: 10 },
  });
  return data.clients ?? [];
};

/**
 * Update an existing client.
 * @param {string} clientId
 * @param {object} body - Partial or full client fields
 * @returns {Promise<object>} Updated client
 */
export const updateClient = async (clientId, body) => {
  const { data } = await apiClient.put(`/clients/${clientId}`, body);
  return data;
};

/**
 * Delete a client.
 * @param {string} clientId
 * @returns {Promise<object>}
 */
export const deleteClient = async (clientId) => {
  const { data } = await apiClient.delete(`/clients/${clientId}`);
  return data;
};

/**
 * Fetch client ledger / statement.
 * @param {string} clientId
 * @param {object} params
 */
export const getClientLedger = async (clientId, params = {}) => {
  const { data } = await apiClient.get(`/clients/${clientId}/ledger`, {
    params,
  });
  return data;
};

/**
 * Fetch client receivable balance.
 * @param {string} clientId
 * @param {object} params
 */
export const getClientBalance = async (clientId, params = {}) => {
  const { data } = await apiClient.get(`/clients/${clientId}/balance`, {
    params,
  });
  return data;
};
